import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPaintBrush, FaProjectDiagram, FaNewspaper, FaUsers, FaUserShield, FaHistory, FaBars } from 'react-icons/fa';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

const API_BASE_URL = 'https://api.4fingerstudios.com';

// Helper function for authenticated API requests (unchanged)
const makeAuthRequest = async (url, method, body = null) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
  };
  if (body) {
    headers['Content-Type'] = 'application/json';
  }
  const options = {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
  };
  const response = await fetch(url, options);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP error! status: ${response.status}`);
  }
  if (response.status === 204) return null;
  return response.json();
};

// Toast Notification System (updated for mobile)
const Toast = ({ id, message, type, onClose }) => {
  const bgColor = type === 'error' ? 'bg-red-600' : 'bg-green-600';
  return (
    <div 
      className={`${bgColor} text-white px-4 py-2 sm:px-6 sm:py-4 rounded-lg shadow-lg flex items-center justify-between w-full max-w-xs sm:max-w-md mb-2 sm:mb-4`}
    >
      <span className="text-sm sm:text-base">{message}</span>
      <button 
        onClick={() => onClose(id)} 
        className="ml-2 sm:ml-4 text-white hover:text-gray-200 focus:outline-none"
      >
        ✕
      </button>
    </div>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed bottom-2 sm:bottom-4 right-2 sm:right-4 z-50 w-full sm:w-auto px-2 sm:px-0">
      <TransitionGroup>
        {toasts.map(toast => (
          <CSSTransition
            key={toast.id}
            timeout={300}
            classNames={{
              enter: 'transform translate-y-full opacity-0',
              enterActive: 'transform translate-y-0 opacity-100 transition-all duration-300 ease-out',
              exit: 'transform translate-y-0 opacity-100',
              exitActive: 'transform translate-y-full opacity-0 transition-all duration-300 ease-in'
            }}
          >
            <Toast {...toast} onClose={removeToast} />
          </CSSTransition>
        ))}
      </TransitionGroup>
    </div>
  );
};

// Modal Component (updated for mobile)
const Modal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-2 sm:p-4">
      <div className="bg-gray-800 p-4 sm:p-6 rounded-xl text-white w-full max-w-md sm:max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {title && <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-purple-300">{title}</h3>}
        {children}
        <button 
          onClick={onClose} 
          className="mt-4 sm:mt-6 w-full py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors text-sm sm:text-base"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// Sidebar Component (updated with mobile toggle)
const Sidebar = ({ activeSection, setActiveSection, isOpen, toggleSidebar }) => {
  const sections = [
    { id: 'art', name: 'Art', icon: <FaPaintBrush /> },
    { id: 'projects', name: 'Projects', icon: <FaProjectDiagram /> },
    { id: 'news', name: 'News', icon: <FaNewspaper /> },
    { id: 'team', name: 'Team', icon: <FaUsers /> },
    { id: 'users', name: 'Users', icon: <FaUserShield /> },
    { id: 'audit', name: 'Audit Log', icon: <FaHistory /> },
  ];

  return (
    <div className={`fixed inset-y-0 left-0 w-64 bg-gray-900 p-4 sm:p-6 flex flex-col shadow-lg transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} sm:translate-x-0 transition-transform duration-300 z-50`}>
      <div className="flex justify-between items-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-purple-300">Admin Panel</h2>
        <button onClick={toggleSidebar} className="sm:hidden text-white">
          <FaBars size={24} />
        </button>
      </div>
      <nav className="space-y-2">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => { setActiveSection(section.id); toggleSidebar(); }}
            className={`w-full text-left py-2 sm:py-3 px-3 sm:px-4 rounded-lg flex items-center space-x-2 sm:space-x-3 transition-colors text-sm sm:text-base ${
              activeSection === section.id 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
            }`}
          >
            {section.icon}
            <span>{section.name}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

// ArtManager Component (unchanged logic, minor style tweaks)
const ArtManager = ({ addToast }) => {
  const [artList, setArtList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newArt, setNewArt] = useState({ name: '', author: '', imageUrl: '' });
  const [editArt, setEditArt] = useState(null);

  useEffect(() => {
    fetchArt();
  }, []);

  const fetchArt = async () => {
    try {
      setIsLoading(true);
      const data = await makeAuthRequest(`${API_BASE_URL}/api/admin/art`, 'GET');
      setArtList(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddArt = async (e) => {
    e.preventDefault();
    try {
      await makeAuthRequest(`${API_BASE_URL}/api/admin/art`, 'POST', newArt);
      setNewArt({ name: '', author: '', imageUrl: '' });
      setIsAddModalOpen(false);
      addToast('Art added successfully', 'success');
      fetchArt();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleEditArt = (art) => {
    setEditArt(art);
    setIsEditModalOpen(true);
  };

  const handleUpdateArt = async (e) => {
    e.preventDefault();
    try {
      await makeAuthRequest(`${API_BASE_URL}/api/admin/art/${editArt.id}`, 'PUT', editArt);
      setIsEditModalOpen(false);
      setEditArt(null);
      addToast('Art updated successfully', 'success');
      fetchArt();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleDeleteArt = async (id) => {
    if (window.confirm('Are you sure you want to delete this art?')) {
      try {
        await makeAuthRequest(`${API_BASE_URL}/api/admin/art/${id}`, 'DELETE');
        addToast('Art deleted successfully', 'success');
        fetchArt();
      } catch (err) {
        addToast(err.message, 'error');
      }
    }
  };

  if (isLoading) return <p className="text-gray-400 text-center">Loading art...</p>;
  if (error) return <p className="text-red-500 text-center">Error: {error}</p>;

  return (
    <div className="bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold text-purple-300 mb-4 sm:mb-6">Manage Art</h2>
      <button 
        onClick={() => setIsAddModalOpen(true)} 
        className="mb-4 sm:mb-6 bg-purple-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
      >
        Add Art
      </button>
      <ul className="space-y-2 sm:space-y-3">
        {artList.map(art => (
          <li key={art.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gray-800 p-3 rounded-lg space-y-2 sm:space-y-0">
            <span className="text-white text-sm sm:text-base">{art.name} by {art.author}</span>
            <div className="flex space-x-2 sm:space-x-3">
              <button onClick={() => handleEditArt(art)} className="text-blue-400 hover:text-blue-300 text-sm sm:text-base">
                Edit
              </button>
              <button onClick={() => handleDeleteArt(art.id)} className="text-red-500 hover:text-red-400 text-sm sm:text-base">
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Art">
        <form onSubmit={handleAddArt}>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Name</label>
            <input
              type="text"
              value={newArt.name}
              onChange={(e) => setNewArt({ ...newArt, name: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter art name"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Author</label>
            <input
              type="text"
              value={newArt.author}
              onChange={(e) => setNewArt({ ...newArt, author: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter author name"
              required
            />
          </div>
          <div className="mb-4 sm:mb-6">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Image URL</label>
            <input
              type="url"
              value={newArt.imageUrl}
              onChange={(e) => setNewArt({ ...newArt, imageUrl: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="https://example.com/image.jpg"
              required
            />
          </div>
          <button type="submit" className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base">
            Add
          </button>
        </form>
      </Modal>
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Art">
        {editArt && (
          <form onSubmit={handleUpdateArt}>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Name</label>
              <input
                type="text"
                value={editArt.name}
                onChange={(e) => setEditArt({ ...editArt, name: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter art name"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Author</label>
              <input
                type="text"
                value={editArt.author}
                onChange={(e) => setEditArt({ ...editArt, author: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter author name"
                required
              />
            </div>
            <div className="mb-4 sm:mb-6">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Image URL</label>
              <input
                type="url"
                value={editArt.imageUrl}
                onChange={(e) => setEditArt({ ...editArt, imageUrl: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="https://example.com/image.jpg"
                required
              />
            </div>
            <button type="submit" className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base">
              Update
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
};

// ProjectManager Component (similar tweaks as ArtManager)
const ProjectManager = ({ addToast }) => {
  const [projectList, setProjectList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newProject, setNewProject] = useState({ 
    name: '', 
    genres: [], 
    phase: '', 
    released: false, 
    previewImage: '',
    projectLinks: [] 
  });
  const [editProject, setEditProject] = useState(null);
  const availableGenres = ['Action', 'Adventure', 'RPG', 'Strategy', 'Simulation', 'Puzzle'];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setIsLoading(true);
      const data = await makeAuthRequest(`${API_BASE_URL}/api/admin/projects`, 'GET');
      setProjectList(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    try {
      await makeAuthRequest(`${API_BASE_URL}/api/admin/projects`, 'POST', newProject);
      setNewProject({ 
        name: '', 
        genres: [], 
        phase: '', 
        released: false, 
        previewImage: '',
        projectLinks: [] 
      });
      setIsAddModalOpen(false);
      addToast('Project added successfully', 'success');
      fetchProjects();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleEditProject = (project) => {
    setEditProject(project);
    setIsEditModalOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      await makeAuthRequest(`${API_BASE_URL}/api/admin/projects/${editProject.id}`, 'PUT', editProject);
      setIsEditModalOpen(false);
      setEditProject(null);
      addToast('Project updated successfully', 'success');
      fetchProjects();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await makeAuthRequest(`${API_BASE_URL}/api/admin/projects/${id}`, 'DELETE');
        addToast('Project deleted successfully', 'success');
        fetchProjects();
      } catch (err) {
        addToast(err.message, 'error');
      }
    }
  };

  const handleAddLink = (type) => {
    if (type === 'new') {
      setNewProject({ ...newProject, projectLinks: [...(newProject.projectLinks || []), ''] });
    } else {
      setEditProject({ ...editProject, projectLinks: [...(editProject.projectLinks || []), ''] });
    }
  };

  const handleRemoveLink = (type, index) => {
    if (type === 'new') {
      const updatedLinks = newProject.projectLinks.filter((_, i) => i !== index);
      setNewProject({ ...newProject, projectLinks: updatedLinks });
    } else {
      const updatedLinks = editProject.projectLinks.filter((_, i) => i !== index);
      setEditProject({ ...editProject, projectLinks: updatedLinks });
    }
  };

  const handleLinkChange = (type, index, value) => {
    if (type === 'new') {
      const updatedLinks = [...newProject.projectLinks];
      updatedLinks[index] = value;
      setNewProject({ ...newProject, projectLinks: updatedLinks });
    } else {
      const updatedLinks = [...editProject.projectLinks];
      updatedLinks[index] = value;
      setEditProject({ ...editProject, projectLinks: updatedLinks });
    }
  };

  if (isLoading) return <p className="text-gray-400 text-center">Loading projects...</p>;
  if (error) return <p className="text-red-500 text-center">Error: {error}</p>;

  return (
    <div className="bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold text-purple-300 mb-4 sm:mb-6">Manage Projects</h2>
      <button 
        onClick={() => setIsAddModalOpen(true)} 
        className="mb-4 sm:mb-6 bg-purple-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
      >
        Add Project
      </button>
      <ul className="space-y-2 sm:space-y-3">
        {projectList.map(project => (
          <li key={project.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gray-800 p-3 rounded-lg space-y-2 sm:space-y-0">
            <span className="text-white text-sm sm:text-base">{project.name} - {project.phase}</span>
            <div className="flex space-x-2 sm:space-x-3">
              <button onClick={() => handleEditProject(project)} className="text-blue-400 hover:text-blue-300 text-sm sm:text-base">
                Edit
              </button>
              <button onClick={() => handleDeleteProject(project.id)} className="text-red-500 hover:text-red-400 text-sm sm:text-base">
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Project">
        <form onSubmit={handleAddProject}>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Name</label>
            <input
              type="text"
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter project name"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Genres</label>
            <select
              multiple
              value={newProject.genres}
              onChange={(e) => {
                const selectedGenres = Array.from(e.target.selectedOptions, option => option.value);
                setNewProject({ ...newProject, genres: selectedGenres });
              }}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
            >
              {availableGenres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Phase</label>
            <input
              type="text"
              value={newProject.phase}
              onChange={(e) => setNewProject({ ...newProject, phase: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter project phase"
            />
          </div>
          <div className="mb-4 flex items-center">
            <label className="mr-2 text-gray-300 text-sm sm:text-base">Released</label>
            <input
              type="checkbox"
              checked={newProject.released}
              onChange={(e) => setNewProject({ ...newProject, released: e.target.checked })}
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Preview Image URL</label>
            <input
              type="url"
              value={newProject.previewImage}
              onChange={(e) => setNewProject({ ...newProject, previewImage: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div className="mb-4 sm:mb-6">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Project Links</label>
            {(newProject.projectLinks || []).map((link, index) => (
              <div key={index} className="flex mb-2">
                <input
                  type="url"
                  value={link}
                  onChange={(e) => handleLinkChange('new', index, e.target.value)}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                  placeholder="https://example.com"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveLink('new', index)}
                  className="ml-2 px-2 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleAddLink('new')}
              className="mt-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm sm:text-base"
            >
              Add Link
            </button>
          </div>
          <button type="submit" className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base">
            Add
          </button>
        </form>
      </Modal>
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Project">
        {editProject && (
          <form onSubmit={handleUpdateProject}>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Name</label>
              <input
                type="text"
                value={editProject.name}
                onChange={(e) => setEditProject({ ...editProject, name: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter project name"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Genres</label>
              <select
                multiple
                value={editProject.genres}
                onChange={(e) => {
                  const selectedGenres = Array.from(e.target.selectedOptions, option => option.value);
                  setEditProject({ ...editProject, genres: selectedGenres });
                }}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              >
                {availableGenres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Phase</label>
              <input
                type="text"
                value={editProject.phase}
                onChange={(e) => setEditProject({ ...editProject, phase: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter project phase"
              />
            </div>
            <div className="mb-4 flex items-center">
              <label className="mr-2 text-gray-300 text-sm sm:text-base">Released</label>
              <input
                type="checkbox"
                checked={editProject.released}
                onChange={(e) => setEditProject({ ...editProject, released: e.target.checked })}
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Preview Image URL</label>
              <input
                type="url"
                value={editProject.previewImage}
                onChange={(e) => setEditProject({ ...editProject, previewImage: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="mb-4 sm:mb-6">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Project Links</label>
              {(editProject.projectLinks || []).map((link, index) => (
                <div key={index} className="flex mb-2">
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleLinkChange('edit', index, e.target.value)}
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                    placeholder="https://example.com"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveLink('edit', index)}
                    className="ml-2 px-2 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleAddLink('edit')}
                className="mt-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm sm:text-base"
              >
                Add Link
              </button>
            </div>
            <button type="submit" className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base">
              Update
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
};

// NewsManager Component (similar tweaks)
const NewsManager = ({ addToast }) => {
  const [newsList, setNewsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newNews, setNewNews] = useState({ headerImage: '', category: '', title: '', description: '', paragraph: '' });
  const [editNews, setEditNews] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setIsLoading(true);
      const data = await makeAuthRequest(`${API_BASE_URL}/api/admin/news`, 'GET');
      setNewsList(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNews = async (e) => {
    e.preventDefault();
    try {
      await makeAuthRequest(`${API_BASE_URL}/api/admin/news`, 'POST', newNews);
      setNewNews({ headerImage: '', category: '', title: '', description: '', paragraph: '' });
      setIsAddModalOpen(false);
      addToast('News added successfully', 'success');
      fetchNews();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleEditNews = (news) => {
    setEditNews(news);
    setIsEditModalOpen(true);
  };

  const handleUpdateNews = async (e) => {
    e.preventDefault();
    try {
      await makeAuthRequest(`${API_BASE_URL}/api/admin/news/${editNews.id}`, 'PUT', editNews);
      setIsEditModalOpen(false);
      setEditNews(null);
      addToast('News updated successfully', 'success');
      fetchNews();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleDeleteNews = async (id) => {
    if (window.confirm('Are you sure you want to delete this news item?')) {
      try {
        await makeAuthRequest(`${API_BASE_URL}/api/admin/news/${id}`, 'DELETE');
        addToast('News deleted successfully', 'success');
        fetchNews();
      } catch (err) {
        addToast(err.message, 'error');
      }
    }
  };

  if (isLoading) return <p className="text-gray-400 text-center">Loading news...</p>;
  if (error) return <p className="text-red-500 text-center">Error: {error}</p>;

  return (
    <div className="bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold text-purple-300 mb-4 sm:mb-6">Manage News</h2>
      <button 
        onClick={() => setIsAddModalOpen(true)} 
        className="mb-4 sm:mb-6 bg-purple-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
      >
        Add News
      </button>
      <ul className="space-y-2 sm:space-y-3">
        {newsList.map(news => (
          <li key={news.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gray-800 p-3 rounded-lg space-y-2 sm:space-y-0">
            <span className="text-white text-sm sm:text-base">{news.title}</span>
            <div className="flex space-x-2 sm:space-x-3">
              <button onClick={() => handleEditNews(news)} className="text-blue-400 hover:text-blue-300 text-sm sm:text-base">
                Edit
              </button>
              <button onClick={() => handleDeleteNews(news.id)} className="text-red-500 hover:text-red-400 text-sm sm:text-base">
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New News">
        <form onSubmit={handleAddNews}>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Header Image URL</label>
            <input
              type="url"
              value={newNews.headerImage}
              onChange={(e) => setNewNews({ ...newNews, headerImage: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Category</label>
            <input
              type="text"
              value={newNews.category}
              onChange={(e) => setNewNews({ ...newNews, category: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter category"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Title</label>
            <input
              type="text"
              value={newNews.title}
              onChange={(e) => setNewNews({ ...newNews, title: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter news title"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Description</label>
            <input
              type="text"
              value={newNews.description}
              onChange={(e) => setNewNews({ ...newNews, description: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter short description"
            />
          </div>
          <div className="mb-4 sm:mb-6">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Paragraph</label>
            <textarea
              value={newNews.paragraph}
              onChange={(e) => setNewNews({ ...newNews, paragraph: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              rows="4"
              placeholder="Enter full news content"
            />
          </div>
          <button type="submit" className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base">
            Add
          </button>
        </form>
      </Modal>
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit News">
        {editNews && (
          <form onSubmit={handleUpdateNews}>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Header Image URL</label>
              <input
                type="url"
                value={editNews.headerImage}
                onChange={(e) => setEditNews({ ...editNews, headerImage: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Category</label>
              <input
                type="text"
                value={editNews.category}
                onChange={(e) => setEditNews({ ...editNews, category: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter category"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Title</label>
              <input
                type="text"
                value={editNews.title}
                onChange={(e) => setEditNews({ ...editNews, title: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter news title"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Description</label>
              <input
                type="text"
                value={editNews.description}
                onChange={(e) => setEditNews({ ...editNews, description: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter short description"
              />
            </div>
            <div className="mb-4 sm:mb-6">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Paragraph</label>
              <textarea
                value={editNews.paragraph}
                onChange={(e) => setEditNews({ ...editNews, paragraph: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                rows="4"
                placeholder="Enter full news content"
              />
            </div>
            <button type="submit" className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base">
              Update
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
};

// TeamManager Component (similar tweaks)
const TeamManager = ({ addToast }) => {
  const [teamList, setTeamList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({ profileImage: '', name: '', role: '', description: '', externalLinks: [''] });
  const [editTeamMember, setEditTeamMember] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setIsLoading(true);
      const data = await makeAuthRequest(`${API_BASE_URL}/api/admin/teams`, 'GET');
      setTeamList(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTeamMember = async (e) => {
    e.preventDefault();
    try {
      await makeAuthRequest(`${API_BASE_URL}/api/admin/teams`, 'POST', newTeamMember);
      setNewTeamMember({ profileImage: '', name: '', role: '', description: '', externalLinks: [''] });
      setIsAddModalOpen(false);
      addToast('Team member added successfully', 'success');
      fetchTeams();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleEditTeamMember = (member) => {
    setEditTeamMember({ ...member, externalLinks: member.externalLinks || [''] });
    setIsEditModalOpen(true);
  };

  const handleUpdateTeamMember = async (e) => {
    e.preventDefault();
    try {
      await makeAuthRequest(`${API_BASE_URL}/api/admin/teams/${editTeamMember.id}`, 'PUT', editTeamMember);
      setIsEditModalOpen(false);
      setEditTeamMember(null);
      addToast('Team member updated successfully', 'success');
      fetchTeams();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleDeleteTeamMember = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await makeAuthRequest(`${API_BASE_URL}/api/admin/teams/${id}`, 'DELETE');
        addToast('Team member deleted successfully', 'success');
        fetchTeams();
      } catch (err) {
        addToast(err.message, 'error');
      }
    }
  };

  const addLink = (isEdit = false) => {
    if (isEdit) {
      setEditTeamMember({
        ...editTeamMember,
        externalLinks: [...editTeamMember.externalLinks, '']
      });
    } else {
      setNewTeamMember({
        ...newTeamMember,
        externalLinks: [...newTeamMember.externalLinks, '']
      });
    }
  };

  const removeLink = (index, isEdit = false) => {
    if (isEdit) {
      const updatedLinks = editTeamMember.externalLinks.filter((_, i) => i !== index);
      setEditTeamMember({ ...editTeamMember, externalLinks: updatedLinks });
    } else {
      const updatedLinks = newTeamMember.externalLinks.filter((_, i) => i !== index);
      setNewTeamMember({ ...newTeamMember, externalLinks: updatedLinks });
    }
  };

  const handleLinkChange = (index, value, isEdit = false) => {
    if (isEdit) {
      const updatedLinks = [...editTeamMember.externalLinks];
      updatedLinks[index] = value;
      setEditTeamMember({ ...editTeamMember, externalLinks: updatedLinks });
    } else {
      const updatedLinks = [...newTeamMember.externalLinks];
      updatedLinks[index] = value;
      setNewTeamMember({ ...newTeamMember, externalLinks: updatedLinks });
    }
  };

  if (isLoading) return <p className="text-gray-400 text-center">Loading team members...</p>;
  if (error) return <p className="text-red-500 text-center">Error: {error}</p>;

  return (
    <div className="bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold text-purple-300 mb-4 sm:mb-6">Manage Team Members</h2>
      <button 
        onClick={() => setIsAddModalOpen(true)} 
        className="mb-4 sm:mb-6 bg-purple-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
      >
        Add Team Member
      </button>
      <ul className="space-y-2 sm:space-y-3">
        {teamList.map(member => (
          <li key={member.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gray-800 p-3 rounded-lg space-y-2 sm:space-y-0">
            <span className="text-white text-sm sm:text-base">{member.name} - {member.role}</span>
            <div className="flex space-x-2 sm:space-x-3">
              <button onClick={() => handleEditTeamMember(member)} className="text-blue-400 hover:text-blue-300 text-sm sm:text-base">
                Edit
              </button>
              <button onClick={() => handleDeleteTeamMember(member.id)} className="text-red-500 hover:text-red-400 text-sm sm:text-base">
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Team Member">
        <form onSubmit={handleAddTeamMember}>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Profile Image URL</label>
            <input
              type="url"
              value={newTeamMember.profileImage}
              onChange={(e) => setNewTeamMember({ ...newTeamMember, profileImage: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Name</label>
            <input
              type="text"
              value={newTeamMember.name}
              onChange={(e) => setNewTeamMember({ ...newTeamMember, name: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter team member name"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Role</label>
            <input
              type="text"
              value={newTeamMember.role}
              onChange={(e) => setNewTeamMember({ ...newTeamMember, role: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              placeholder="Enter role"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">Description</label>
            <textarea
              value={newTeamMember.description}
              onChange={(e) => setNewTeamMember({ ...newTeamMember, description: e.target.value })}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
              rows="3"
              placeholder="Enter description"
            />
          </div>
          <div className="mb-4 sm:mb-6">
            <label className="block mb-2 text-gray-300 text-sm sm:text-base">External Links</label>
            {newTeamMember.externalLinks.map((link, index) => (
              <div key={index} className="flex mb-2">
                <input
                  type="url"
                  value={link}
                  onChange={(e) => handleLinkChange(index, e.target.value)}
                  className="flex-grow p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                  placeholder="https://example.com"
                />
                <button 
                  type="button" 
                  onClick={() => removeLink(index)} 
                  className="ml-2 text-red-500 hover:text-red-400 text-sm sm:text-base"
                >
                  Remove
                </button>
              </div>
            ))}
            <button 
              type="button" 
              onClick={() => addLink()} 
              className="mt-2 text-blue-400 hover:text-blue-300 text-sm sm:text-base"
            >
              Add Link
            </button>
          </div>
          <button type="submit" className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base">
            Add
          </button>
        </form>
      </Modal>
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Team Member">
        {editTeamMember && (
          <form onSubmit={handleUpdateTeamMember}>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Profile Image URL</label>
              <input
                type="url"
                value={editTeamMember.profileImage}
                onChange={(e) => setEditTeamMember({ ...editTeamMember, profileImage: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Name</label>
              <input
                type="text"
                value={editTeamMember.name}
                onChange={(e) => setEditTeamMember({ ...editTeamMember, name: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter team member name"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Role</label>
              <input
                type="text"
                value={editTeamMember.role}
                onChange={(e) => setEditTeamMember({ ...editTeamMember, role: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                placeholder="Enter role"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">Description</label>
              <textarea
                value={editTeamMember.description}
                onChange={(e) => setEditTeamMember({ ...editTeamMember, description: e.target.value })}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                rows="3"
                placeholder="Enter description"
              />
            </div>
            <div className="mb-4 sm:mb-6">
              <label className="block mb-2 text-gray-300 text-sm sm:text-base">External Links</label>
              {editTeamMember.externalLinks.map((link, index) => (
                <div key={index} className="flex mb-2">
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleLinkChange(index, e.target.value, true)}
                    className="flex-grow p-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                    placeholder="https://example.com"
                  />
                  <button 
                    type="button" 
                    onClick={() => removeLink(index, true)} 
                    className="ml-2 text-red-500 hover:text-red-400 text-sm sm:text-base"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                onClick={() => addLink(true)} 
                className="mt-2 text-blue-400 hover:text-blue-300 text-sm sm:text-base"
              >
                Add Link
              </button>
            </div>
            <button type="submit" className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base">
              Update
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
};

// UsersManager Component (similar tweaks)
const UsersManager = ({ addToast }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const data = await makeAuthRequest(`${API_BASE_URL}/api/admin/users`, 'GET');
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (window.confirm(`Are you sure you want to delete user ${username}?`)) {
      try {
        await makeAuthRequest(`${API_BASE_URL}/api/admin/users/${username}`, 'DELETE');
        addToast(`User ${username} deleted`, 'success');
        fetchUsers();
      } catch (err) {
        addToast(err.message, 'error');
      }
    }
  };

  const handlePromoteUser = async (username) => {
    if (window.confirm(`Are you sure you want to promote ${username} to Admin?`)) {
      try {
        await makeAuthRequest(`${API_BASE_URL}/api/auth/promote`, 'POST', { targetUsername: username });
        addToast(`User ${username} promoted to Admin`, 'success');
        fetchUsers();
      } catch (err) {
        addToast(err.message, 'error');
      }
    }
  };

  if (isLoading) return <p className="text-gray-400 text-center">Loading users...</p>;
  if (error) return <p className="text-red-500 text-center">Error: {error}</p>;

  return (
    <div className="bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold text-purple-300 mb-4 sm:mb-6">Manage Users</h2>
      <ul className="space-y-2 sm:space-y-3">
        {users.map(user => (
          <li key={user.username} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gray-800 p-3 rounded-lg space-y-2 sm:space-y-0">
            <span className="text-white text-sm sm:text-base">{user.username} ({user.role})</span>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              {user.role !== 'Admin' && (
                <button
                  onClick={() => handlePromoteUser(user.username)}
                  className="text-green-400 hover:text-green-300 text-sm sm:text-base"
                >
                  Promote to Admin
                </button>
              )}
              <button
                onClick={() => handleDeleteUser(user.username)}
                className="text-red-500 hover:text-red-400 text-sm sm:text-base"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

// AuditLogManager Component (updated for mobile table)
const AuditLogManager = ({ addToast }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      setIsLoading(true);
      const data = await makeAuthRequest(`${API_BASE_URL}/api/auth/audit-log`, 'GET');
      setAuditLogs(data);
    } catch (err) {
      setError(err.message);
      addToast(err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <p className="text-gray-400 text-center">Loading audit logs...</p>;
  if (error) return <p className="text-red-500 text-center">Error: {error}</p>;

  return (
    <div className="bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold text-purple-300 mb-4 sm:mb-6">Audit Log</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-white text-sm sm:text-base">
          <thead>
            <tr className="bg-gray-800">
              <th className="p-2 sm:p-3 text-left">Timestamp</th>
              <th className="p-2 sm:p-3 text-left">User</th>
              <th className="p-2 sm:p-3 text-left">Action</th>
              <th className="p-2 sm:p-3 text-left">Details</th>
            </tr>
          </thead>
          <tbody>
            {auditLogs.map((log, index) => (
              <tr key={index} className="border-b border-gray-800 hover:bg-gray-800">
                <td className="p-2 sm:p-3 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                <td className="p-2 sm:p-3">{log.user}</td>
                <td className="p-2 sm:p-3">{log.action}</td>
                <td className="p-2 sm:p-3">{log.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Main AdminPage Component (updated with mobile sidebar toggle)
const AdminPage = () => {
  const [isAdminValidated, setIsAdminValidated] = useState(null);
  const [activeSection, setActiveSection] = useState('art');
  const [toasts, setToasts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      try {
        const data = await makeAuthRequest(`${API_BASE_URL}/api/auth/validate`, 'GET');
        if (data.valid && data.role === 'Admin') {
          setIsAdminValidated(true);
        } else {
          setIsAdminValidated(false);
        }
      } catch (error) {
        setIsAdminValidated(false);
      }
    };
    validateToken();
  }, []);

  const addToast = (message, type) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (isAdminValidated === null) {
    return <div className="text-gray-400 text-center mt-10">Loading...</div>;
  }

  if (!isAdminValidated) {
    return (
      <div className="text-red-500 text-center mt-10">
        Unauthorized. Please log in as an admin.
      </div>
    );
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'art':
        return <ArtManager addToast={addToast} />;
      case 'projects':
        return <ProjectManager addToast={addToast} />;
      case 'news':
        return <NewsManager addToast={addToast} />;
      case 'team':
        return <TeamManager addToast={addToast} />;
      case 'users':
        return <UsersManager addToast={addToast} />;
      case 'audit':
        return <AuditLogManager addToast={addToast} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col sm:flex-row">
      <Sidebar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection} 
        isOpen={sidebarOpen} 
        toggleSidebar={toggleSidebar} 
      />
      <div className="flex-1 sm:ml-64 p-4 sm:p-8">
        <div className="flex justify-between items-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-purple-300">Admin Dashboard</h1>
          <button onClick={toggleSidebar} className="sm:hidden text-white">
            <FaBars size={24} />
          </button>
        </div>
        {renderSection()}
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default AdminPage;